import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "./ui/Toast";
import { Badge, Button, Card, Modal } from "./ui";
import { ConfirmModal } from "./ui/Modal";

const DOCS_URL = "/api/v1/fornitori-api/docs";

/**
 * Sezione "Integrazione API" nel Profilo di un utente fornitore.
 * Mostra le credenziali create da PHC per il fornitore; l'utente puo' leggere il
 * segreto attuale (senza cambiarlo) o generarne uno nuovo. IP consentiti e creazione
 * restano all'admin.
 */
function IntegrazioneApiProfilo({ fornitoreId }) {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();
  const base = `/fornitori/${fornitoreId}/credenziali-api`;

  const [credenziali, setCredenziali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [segreto, setSegreto] = useState(null); // { id, segreto }
  const [daRuotare, setDaRuotare] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(base);
      setCredenziali(data.value || []);
    } catch {
      setCredenziali([]);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, base]);

  useEffect(() => {
    load();
  }, [load]);

  const mostra = async (c) => {
    try {
      setSegreto(
        await apiRequest(`${base}/${c.id}/segreto`, { method: "POST" }),
      );
    } catch (error) {
      showToast(error.message || "Segreto non disponibile", "error");
    }
  };

  const ruota = async () => {
    setSaving(true);
    try {
      const res = await apiRequest(`${base}/${daRuotare.id}/ruota`, {
        method: "POST",
      });
      setDaRuotare(null);
      setSegreto(res);
    } catch (error) {
      showToast(error.message || "Operazione non riuscita", "error");
    } finally {
      setSaving(false);
    }
  };

  const copia = async (testo) => {
    try {
      await navigator.clipboard.writeText(testo);
      showToast("Copiato negli appunti", "success");
    } catch {
      showToast("Copia non riuscita: seleziona e copia a mano", "warning");
    }
  };

  return (
    <Card title="Integrazione API" className="mt-6">
      <p className="text-sm text-gray-600 mb-4">
        Con queste credenziali il vostro gestionale puo' inviare interventi e
        rapportini al portale senza passare dall'interfaccia. Istruzioni
        tecniche, firma delle richieste ed esempi di codice nella{" "}
        <a
          href={DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 underline"
        >
          documentazione API
        </a>
        .
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">Caricamento...</p>
      ) : credenziali.length === 0 ? (
        <p className="text-sm text-gray-500">
          Nessuna credenziale attiva. Per richiederla scrivete a{" "}
          <a
            href="mailto:automation@peralbahc.com"
            className="text-primary-600 underline"
          >
            automation@peralbahc.com
          </a>{" "}
          indicando gli indirizzi IP pubblici da cui chiamerete l'API.
        </p>
      ) : (
        <div className="space-y-3">
          {credenziali.map((c) => (
            <div
              key={c.id}
              className="border border-gray-200 rounded-lg p-4 flex flex-wrap gap-4 justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.nome}</span>
                  <Badge variant={c.attiva ? "success" : "default"}>
                    {c.attiva ? "Attiva" : "Disattivata"}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 break-all">
                  Key Id: <code>{c.id}</code>{" "}
                  <button
                    type="button"
                    className="text-primary-600 underline"
                    onClick={() => copia(c.id)}
                  >
                    copia
                  </button>
                </p>
                <p className="text-xs text-gray-500">
                  IP consentiti:{" "}
                  {c.ipConsentiti
                    ? c.ipConsentiti.split(/[\s,;]+/).join(", ")
                    : "nessuna restrizione"}
                </p>
              </div>
              {c.attiva && (
                <div className="flex flex-wrap items-start gap-2">
                  <Button size="sm" variant="outline" onClick={() => mostra(c)}>
                    Mostra segreto
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDaRuotare(c)}
                  >
                    Genera nuovo segreto
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={!!segreto}
        onClose={() => setSegreto(null)}
        title="Segreto della credenziale"
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setSegreto(null)}>Chiudi</Button>
          </div>
        }
      >
        {segreto && (
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Custodite il segreto come una password: serve solo al vostro
              sistema per firmare le richieste e non va mai inviato nelle
              chiamate.
            </div>
            {[
              ["Key Id", segreto.id],
              ["Segreto", segreto.segreto],
            ].map(([etichetta, valore]) => (
              <div key={etichetta}>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {etichetta}
                </p>
                <div className="flex gap-2">
                  <code className="flex-1 p-2 bg-gray-100 rounded text-xs break-all">
                    {valore}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copia(valore)}
                  >
                    Copia
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!daRuotare}
        onClose={() => setDaRuotare(null)}
        onConfirm={ruota}
        loading={saving}
        title="Generare un nuovo segreto?"
        message="Il segreto attuale smette di funzionare subito: il vostro sistema non potra' piu' inviare interventi finche' non sara' aggiornato con il nuovo segreto. Usatelo solo se il segreto attuale e' compromesso o se state configurando l'integrazione."
        confirmText="Genera"
        confirmVariant="danger"
      />
    </Card>
  );
}

export default IntegrazioneApiProfilo;
